import { LinkedinFilled } from "@ant-design/icons"
import { Avatar, Dropdown, Empty, Skeleton, Tabs } from "antd"
import {
  BookOpen,
  ChevronDownIcon,
  Crosshair,
  GraduationCap,
  Lightbulb,
  Mail,
  Newspaper,
  Target,
  User,
  MessageCircle,
  Layers,
  TrendingUp,
  Share2
} from "lucide-react"
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import linkedinLogo from "~/assets/linkedin.png"
import MarkdownRender from "~/components/markdown-render"
import { GetEmailButton } from "~/components/recommended-person"
import useChampionsTrackingStore from "~/store/champions-tracking"
import useGlobalStore from "~/store/global"
import { EmailTemplateType } from "~/types/accounts"
import { getLocationDisplay } from "~/utils/location-helpers"

import { isValidPriorityItem } from "../knowledge-os/pre-meeting/pre-meeting"
import { Section } from "./section"
import { SECTION_STYLES, SectionHeader } from "./section-ui"
import { CoachingPackSection } from "./sections/coaching-pack-section"
import { TechStackSection } from "./sections/tech-stack-section"
import { IndustryTrendsSection } from "./sections/industry-trends-section"
import { SocialMediaSection } from "./sections/social-media-section"
import { usePmbSection } from "~/hooks/pre-meeting"
import type {
  CoachingPackPayload,
  TechStackPayload,
  IndustryTrendsPayload,
  SocialMediaPayload
} from "~/types/pre-meeting"

const AccountBrief = lazy(() => import("~/containers/account-brief"))

interface PersonBriefProps {
  persona: any
  people: any
  renderMessageTemplate?: any
  setIsOpen: any
  account?: any
  account_name?: string
  accountId?: number
  streaming?: boolean
  personaLoading?: boolean
  // "scroll" (default) keeps every section stacked in one scroll container with
  // the pill row acting as a scroll-spy index — what the drawer and the
  // marketing-os modal have always done, and what they still get.
  //
  // "tabs" shows one section at a time behind an antd Tabs bar, matching the
  // page's own Overview tab. Person 360 renders the brief INLINE in a page that
  // already scrolls, so stacking nine sections made the page enormous and the
  // scroll-spy index fought the page's own scrollbar. Opt-in rather than a
  // straight swap: the drawer is a 70%-width overlay where one long scroll is
  // the right shape.
  layout?: "scroll" | "tabs"
}

export const isValidNewsItemForPerson = (item: any): boolean => {
  if (!item || typeof item !== "object") return false
  const requiredKeys: (keyof typeof item)[] = ["link", "title", "date"]
  return requiredKeys.every(
    (key) => typeof item[key] === "string" && item[key].trim().length > 0
  )
}

function getInitials(name: string): string {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("")
}

// Section navigation config
interface NavSection {
  id: string
  label: string
  icon: React.ReactNode
  styleKey: keyof typeof SECTION_STYLES
  count?: number
  loading?: boolean
}

const PersonBrief = ({
  persona,
  people,
  renderMessageTemplate,
  setIsOpen,
  account,
  account_name,
  accountId,
  streaming,
  personaLoading,
  layout = "scroll"
}: PersonBriefProps) => {
  const { t } = useTranslation()
  const tabbed = layout === "tabs"
  const [activeSection, setActiveSection] = useState<string>("about")
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isProgrammaticScroll = useRef(false)
  const setShowMessageTemplateModal = useChampionsTrackingStore(
    (store) => store.setShowMessageTemplateModal
  )

  const newSectionsEnabled = useGlobalStore(
    (store) => store.user?.feature_flags?.enable_new_pmb_sections
  )

  // Make the account-scoped sections work regardless of which entry point
  // rendered PersonBrief: fall back to account context carried on the person
  // object or the account prop when the explicit props are not passed.
  const rawAccountId =
    accountId ??
    people?.account_id ??
    people?.company_id ??
    people?.organization_id ??
    people?.priorities?.[0]?.account_id ??
    account?.id ??
    account?.account_id
  // ids can arrive as numeric strings (e.g. company_id: "20331")
  const resolvedAccountId =
    rawAccountId == null ? undefined : Number(rawAccountId) || rawAccountId
  const resolvedAccountName =
    account_name ||
    people?.organization_name ||
    people?.company_name ||
    account?.name ||
    account?.account_name ||
    (typeof account === "string" ? account : "")

  const pmbCtx = {
    accountId: resolvedAccountId,
    linkedinUrl: people?.url,
    personTitle: people?.title,
    personContext: {
      fullname: people?.name,
      account_name: resolvedAccountName
    }
  }

  // New PMB v2 sections. The same hooks run in the child section components;
  // React Query dedupes by queryKey, so these reads are free. We use them only
  // to decide whether to show each nav pill (and the child omits its own block
  // when empty), so empty sections never appear as placeholders.
  // Defer the fetch until streaming ends: the query key embeds personTitle/accountId (empty until the summary frame), so firing mid-stream refetches and flickers the capsules.
  const accountSectionsApplicable = !!newSectionsEnabled && !!resolvedAccountId
  const personSectionsApplicable = !!newSectionsEnabled && !!people?.url
  const accountSectionsEnabled = accountSectionsApplicable && !streaming
  const personSectionsEnabled = personSectionsApplicable && !streaming

  const coachingPackQuery = usePmbSection<CoachingPackPayload>(
    "coaching_pack",
    pmbCtx,
    personSectionsEnabled
  )
  const techStackQuery = usePmbSection<TechStackPayload>(
    "tech_stack",
    pmbCtx,
    accountSectionsEnabled
  )
  const industryTrendsQuery = usePmbSection<IndustryTrendsPayload>(
    "industry_trends",
    pmbCtx,
    accountSectionsEnabled
  )
  const socialMediaQuery = usePmbSection<SocialMediaPayload>(
    "social_media",
    pmbCtx,
    personSectionsEnabled
  )

  const hasCoachingPack =
    !!coachingPackQuery.data &&
    !coachingPackQuery.data.error &&
    ((coachingPackQuery.data.payload?.objections?.length ?? 0) > 0 ||
      (coachingPackQuery.data.payload?.questions?.length ?? 0) > 0 ||
      (coachingPackQuery.data.payload?.red_flags?.length ?? 0) > 0 ||
      (coachingPackQuery.data.payload?.industry_landmines?.length ?? 0) > 0)
  const hasTechStack =
    !!techStackQuery.data &&
    !techStackQuery.data.error &&
    (techStackQuery.data.payload?.top?.length ?? 0) > 0
  const hasIndustryTrends =
    !!industryTrendsQuery.data &&
    !industryTrendsQuery.data.error &&
    (industryTrendsQuery.data.payload?.trends?.length ?? 0) > 0
  const hasSocialMedia =
    !!socialMediaQuery.data &&
    !socialMediaQuery.data.error &&
    ((socialMediaQuery.data.payload?.linkedin?.length ?? 0) > 0 ||
      (socialMediaQuery.data.payload?.youtube?.length ?? 0) > 0)

  const coachingLoading =
    personSectionsApplicable &&
    !hasCoachingPack &&
    !(coachingPackQuery.isSuccess || coachingPackQuery.isError)
  const techLoading =
    accountSectionsApplicable &&
    !hasTechStack &&
    !(techStackQuery.isSuccess || techStackQuery.isError)
  const industryLoading =
    accountSectionsApplicable &&
    !hasIndustryTrends &&
    !(industryTrendsQuery.isSuccess || industryTrendsQuery.isError)
  const socialLoading =
    personSectionsApplicable &&
    !hasSocialMedia &&
    !(socialMediaQuery.isSuccess || socialMediaQuery.isError)

  const formattedUrl = people.url?.startsWith("linkedin.com")
    ? `https://www.${people.url}`
    : people.url

  const displayName = getLocationDisplay(people.name) || people.name
  const displayTitle = getLocationDisplay(people.title) || people.title
  const displayLocation = getLocationDisplay(people.location) || people.location

  const validNews = useMemo(
    () =>
      Array.isArray(people.news)
        ? people.news.filter(isValidNewsItemForPerson)
        : [],
    [people.news]
  )
  const validPosts = useMemo(
    () =>
      Array.isArray(people.posts)
        ? people.posts.filter(isValidNewsItemForPerson)
        : [],
    [people.posts]
  )
  const validPriorities = useMemo(
    () =>
      Array.isArray(people.priorities)
        ? people.priorities.filter(isValidPriorityItem)
        : [],
    [people.priorities]
  )
  const combinedNews = useMemo(
    () =>
      validNews.length > 0 || validPosts.length > 0
        ? [...validNews, ...validPosts].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        : [],
    [validNews, validPosts]
  )

  const personalityTraits = persona
    ? {
        summary: persona.summary,
        key_characteristics: persona.key_characteristics
      }
    : null
  const guidelines = persona?.persona?.guidelines || null

  const onClickMessageTemplate = useCallback(
    (templateType: EmailTemplateType) => {
      if (people) {
        setShowMessageTemplateModal({
          ...people,
          linkedin_url: people.url || people.linkedin_url,
          name: people.name,
          templateType,
          opportunity_ids: validPriorities?.map((priority: any) => priority.id)
        })
      }
    },
    [people, validPriorities, setShowMessageTemplateModal]
  )

  // Build nav sections
  const navSections = useMemo<NavSection[]>(() => {
    const sections: NavSection[] = []
    if (people?.summary?.background) {
      sections.push({
        id: "about",
        label: t("label_about"),
        icon: <User size={14} />,
        styleKey: "about"
      })
    } else if (streaming) {
      sections.push({
        id: "about",
        label: t("label_about"),
        icon: <User size={14} />,
        styleKey: "about",
        loading: true
      })
    }
    if (people?.summary?.meeting_tips) {
      sections.push({
        id: "talking_points",
        label: `${t("person_brief.label_talking_points")}`,
        icon: <Lightbulb size={14} />,
        styleKey: "talking_points",
        count: Array.isArray(people.summary.meeting_tips)
          ? people.summary.meeting_tips.length
          : undefined
      })
    } else if (streaming) {
      sections.push({
        id: "talking_points",
        label: `${t("person_brief.label_talking_points")}`,
        icon: <Lightbulb size={14} />,
        styleKey: "talking_points",
        loading: true
      })
    }
    if (validPriorities.length > 0) {
      sections.push({
        id: "key_account_priorities",
        label: t("label_priorities"),
        icon: <Target size={14} />,
        styleKey: "key_account_priorities",
        count: validPriorities.length
      })
    }
    if (personalityTraits && guidelines) {
      sections.push({
        id: "know_your_prospect",
        label: t("person_brief.label_know_your_prospect"),
        icon: <Crosshair size={14} />,
        styleKey: "know_your_prospect"
      })
    } else if (personaLoading) {
      sections.push({
        id: "know_your_prospect",
        label: t("person_brief.label_know_your_prospect"),
        icon: <Crosshair size={14} />,
        styleKey: "know_your_prospect",
        loading: true
      })
    }
    if (account) {
      sections.push({
        id: "account_brief",
        label: t("label_account"),
        icon: <BookOpen size={14} />,
        styleKey: "account_brief"
      })
    }
    if (newSectionsEnabled) {
      if (hasCoachingPack)
        sections.push({
          id: "coaching_pack",
          label: "Coaching Pack",
          icon: <MessageCircle size={14} />,
          styleKey: "coaching_pack"
        })
      else if (coachingLoading)
        sections.push({
          id: "coaching_pack",
          label: "Coaching Pack",
          icon: <MessageCircle size={14} />,
          styleKey: "coaching_pack",
          loading: true
        })
      if (hasTechStack)
        sections.push({
          id: "tech_stack",
          label: "Top Technologies",
          icon: <Layers size={14} />,
          styleKey: "tech_stack"
        })
      else if (techLoading)
        sections.push({
          id: "tech_stack",
          label: "Top Technologies",
          icon: <Layers size={14} />,
          styleKey: "tech_stack",
          loading: true
        })
      if (hasIndustryTrends)
        sections.push({
          id: "industry_trends",
          label: "Industry Trends",
          icon: <TrendingUp size={14} />,
          styleKey: "industry_trends"
        })
      else if (industryLoading)
        sections.push({
          id: "industry_trends",
          label: "Industry Trends",
          icon: <TrendingUp size={14} />,
          styleKey: "industry_trends",
          loading: true
        })
      if (hasSocialMedia)
        sections.push({
          id: "social_media",
          label: "Social Media",
          icon: <Share2 size={14} />,
          styleKey: "social_media"
        })
      else if (socialLoading)
        sections.push({
          id: "social_media",
          label: "Social Media",
          icon: <Share2 size={14} />,
          styleKey: "social_media",
          loading: true
        })
    }
    return sections
  }, [
    people,
    validPriorities,
    personalityTraits,
    guidelines,
    personaLoading,
    streaming,
    account,
    newSectionsEnabled,
    hasCoachingPack,
    hasTechStack,
    hasIndustryTrends,
    hasSocialMedia,
    coachingLoading,
    techLoading,
    industryLoading,
    socialLoading
  ])

  const scrollToSection = useCallback(
    (sectionId: string) => {
      setActiveSection(sectionId)
      // In tabbed mode selecting a tab IS the whole action — the other panels
      // are not on screen to scroll to, and scrolling the page under the user
      // on a tab click is not what a tab does.
      if (tabbed) return

      const doScroll = (attempt = 0) => {
        const el = document.getElementById(sectionId)
        if (!el) {
          if (attempt < 5) setTimeout(() => doScroll(attempt + 1), 150)
          return
        }
        isProgrammaticScroll.current = true
        // Account for the sticky header height (identity + actions + nav pills)
        const stickyHeader = document.querySelector(".pmb-sticky-header")
        const headerHeight = stickyHeader?.getBoundingClientRect().height || 0
        el.style.scrollMarginTop = `${headerHeight + 12}px`
        el.scrollIntoView({ behavior: "smooth", block: "start" })
        setTimeout(() => {
          isProgrammaticScroll.current = false
        }, 800)
      }

      doScroll()
    },
    [tabbed]
  )

  useEffect(() => {
    // The scroll-spy would overwrite the selected tab on every page scroll,
    // because all panels stay mounted (hidden) to keep their streams alive.
    if (tabbed) return
    const container = scrollContainerRef.current
    if (!container || navSections.length === 0) return

    const intersecting = new Set<string>()
    const sectionIds = new Set(navSections.map((s) => s.id))

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return
        entries.forEach((e) => {
          if (e.isIntersecting) intersecting.add(e.target.id)
          else intersecting.delete(e.target.id)
        })
        const active = navSections.find((s) => intersecting.has(s.id))
        if (active) setActiveSection(active.id)
      },
      { root: container, rootMargin: "0px 0px -50% 0px", threshold: 0 }
    )

    const observeExisting = () => {
      navSections
        .map((s) => document.getElementById(s.id))
        .filter(Boolean)
        .forEach((el) => observer.observe(el!))
    }

    observeExisting()

    // Re-observe when new section elements appear in DOM (lazy loads, conditional renders)
    const mutationObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLElement) {
            if (sectionIds.has(node.id)) {
              observer.observe(node)
            }
            // Check children too (for sections nested inside added subtrees)
            for (const id of sectionIds) {
              const child = node.querySelector?.(`#${CSS.escape(id)}`)
              if (child instanceof HTMLElement) observer.observe(child)
            }
          }
        }
      }
    })

    mutationObserver.observe(container, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
    }
  }, [navSections, tabbed])

  // The selected tab has to be one that actually rendered. Sections appear as
  // the brief streams and some never appear at all (no priorities, no account,
  // flags off), so a fixed default of "about" can point at nothing and show an
  // empty panel. Fall back to the first section that exists.
  const activeTab =
    navSections.find((s) => s.id === activeSection)?.id ?? navSections[0]?.id

  // Inactive panels stay MOUNTED and hidden. Unmounting them would abort the
  // brief's stream and drop the lazy section queries every time a tab changed,
  // and the parent reads those same queries to decide which tabs exist at all.
  const panelCls = (id: string) =>
    !tabbed ? "" : activeTab === id ? "animate-tab-panel-in" : "hidden"

  // The rule between sections separates one section from the NEXT one. With one
  // panel on screen there is no next one, so it would draw a line under the
  // whole tab for no reason.
  const sectionCls = (id: string) =>
    `py-8 ${
      tabbed ? "" : "border-b border-light-border/30 dark:border-border/30"
    } ${panelCls(id)}`

  if (!people.url) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Empty
          description={
            <p className="text-13">
              We&apos;re currently gathering information about this account.
              Check back soon for updates.
            </p>
          }
        />
      </div>
    )
  }

  const initials = getInitials(displayName)

  return (
    <div className="flex flex-col dark:bg-dark-1 h-full">
      {/* Sticky header. In tabbed mode all that survives here is the tab bar,
          which draws its own rule — the wrapper's border and shadow would
          double it. It is not sticky either: this sits inline in a page that
          owns the scroll. */}
      <div
        className={
          tabbed
            ? "pmb-sticky-header bg-white dark:bg-dark-1"
            : "pmb-sticky-header sticky top-0 z-10 border-b border-light-border bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:border-border dark:bg-dark-1"
        }
      >
        {/* Identity row — suppressed in tabbed mode. Person 360 renders its own
            header directly above this one: the same avatar, the same LinkedIn
            link, the same name, the same title and location, and the same two
            message actions. Printed twice, one screen apart, it read as two
            different people's cards. The page's header is the one that stays
            because it also carries the email and phone chips and their
            enrichment buttons. */}
        {tabbed ? null : (
          <div
            className="flex items-center gap-3 px-6 pt-4 pb-3"
            data-testid="person-brief-meta"
          >
            <Avatar
              size={48}
              className="!bg-primary !text-white flex-shrink-0 font-semibold"
            >
              {initials}
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                {formattedUrl ? (
                  <a
                    href={formattedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 hover:underline"
                    data-testid="pre-meeting-brief-linkedin-link"
                  >
                    <LinkedinFilled className="text-[#0a66c2] text-sm" />
                    <span
                      className="text-sm font-semibold text-dark-2 dark:text-dark-6"
                      data-testid="pre-meeting-brief-person-name-linked"
                    >
                      {displayName}
                    </span>
                  </a>
                ) : (
                  <span
                    className="text-sm font-semibold text-dark-2 dark:text-dark-6"
                    data-testid="pre-meeting-brief-person-name-plain"
                  >
                    {displayName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                {displayTitle && (
                  <span
                    className="text-xs text-dark-2 dark:text-dark-6 opacity-80 truncate max-w-[220px]"
                    data-testid="pre-meeting-brief-title-person-details"
                  >
                    {displayTitle}
                  </span>
                )}
                {displayTitle && displayLocation && (
                  <span className="text-xs opacity-50">·</span>
                )}
                {displayLocation && (
                  <span
                    className="text-xs text-dark-2 dark:text-dark-6 opacity-60"
                    data-testid="pre-meeting-brief-location"
                  >
                    {displayLocation}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <GetEmailButton
                className="border-[0.5px] border-light-border dark:border-border dark:bg-dark-2 rounded-lg p-2 h-8"
                linkedin_url={people.url}
                key={people.url}
              />
              {renderMessageTemplate ? (
                renderMessageTemplate({ close: () => setIsOpen(false) })
              ) : (
                <Dropdown
                  placement="bottomLeft"
                  trigger={["click"]}
                  menu={{
                    items: [
                      {
                        key: "LINKEDIN",
                        icon: (
                          <img
                            src={linkedinLogo}
                            alt="Linkedin icon"
                            width={12}
                            height={12}
                          />
                        ),
                        label: (
                          <span
                            className="text-xs"
                            data-testid="pre-meeting-brief-linkedin-message"
                          >
                            {t("label_linkedin_message")}
                          </span>
                        ),
                        onClick: () => onClickMessageTemplate?.("LINKEDIN")
                      },
                      {
                        key: "EMAIL",
                        icon: <Mail size={14} strokeWidth={1.5} />,
                        label: (
                          <span
                            className="text-xs"
                            data-testid="pre-meeting-brief-email-message"
                          >
                            {t("label_email_message")}
                          </span>
                        ),
                        onClick: () => onClickMessageTemplate?.("EMAIL")
                      }
                    ]
                  }}
                >
                  <button className="dark:text-dark-6 text-dark-2 p-2 border-[0.5px] border-light-border dark:border-border rounded-lg text-xs dark:bg-dark-2 bg-white flex items-center gap-1">
                    <span>{t("label_generate_message")}</span>
                    <ChevronDownIcon size={14} />
                  </button>
                </Dropdown>
              )}
            </div>
          </div>
        )}

        {/* Section tabs — the same underlined bar as the page's Overview tab,
            so the brief's sections read as tabs of the page rather than a
            second, differently-shaped navigation inside it. */}
        {tabbed && navSections.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={scrollToSection}
            className="px-6 [&_.ant-tabs-nav]:!mb-0"
            items={navSections.map((section) => {
              const s = SECTION_STYLES[section.styleKey]
              return {
                key: section.id,
                disabled: section.loading,
                label: (
                  <span
                    className="flex items-center gap-1.5"
                    data-testid={`pre-meeting-brief-${section.id}${
                      section.loading ? "-loading" : ""
                    }`}
                  >
                    <span
                      className="flex size-[18px] shrink-0 items-center justify-center rounded-md"
                      style={{ color: s.hex }}
                    >
                      {section.loading ? (
                        <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        section.icon
                      )}
                    </span>
                    {section.label}
                    {section.count !== undefined && (
                      <span
                        className="flex size-4 items-center justify-center rounded-full text-[10px] leading-none text-white"
                        style={{ background: s.hex }}
                      >
                        {section.count}
                      </span>
                    )}
                  </span>
                )
              }
            })}
          />
        ) : null}

        {/* Section index pills */}
        {!tabbed && navSections.length > 0 && (
          <div className="flex items-center gap-2 px-6 pb-3 overflow-x-auto scrollbar-none">
            {navSections.map((section) => {
              const s = SECTION_STYLES[section.styleKey]
              const isActive = activeSection === section.id
              if (section.loading) {
                return (
                  <button
                    key={section.id}
                    disabled
                    data-testid={`pre-meeting-brief-${section.id}-loading`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-transparent bg-[#f5f5f5] dark:bg-dark-2 text-dark-2 dark:text-dark-6 opacity-70 cursor-default flex-shrink-0"
                  >
                    <span className="w-[18px] h-[18px] rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    </span>
                    {section.label}
                  </button>
                )
              }
              return (
                <button
                  key={section.id}
                  data-testid={`pre-meeting-brief-${section.id}`}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex-shrink-0 ${
                    isActive
                      ? "border-primary/30 dark:border-primary-light/30 bg-genesis dark:bg-dark-3 text-primary dark:text-primary-light"
                      : "border-transparent bg-[#f5f5f5] dark:bg-dark-2 text-dark-2 dark:text-dark-6 hover:border-light-border dark:hover:border-border"
                  }`}
                >
                  <span
                    className="w-[18px] h-[18px] rounded-md flex items-center justify-center flex-shrink-0"
                    style={
                      isActive
                        ? { background: s.bg, color: s.hex }
                        : { color: "currentColor", opacity: 0.6 }
                    }
                  >
                    {section.icon}
                  </span>
                  {section.label}
                  {section.count !== undefined && (
                    <span
                      className="w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center leading-none"
                      style={{ background: s.hex }}
                    >
                      {section.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollContainerRef}
        id="brief-container"
        className={`flex flex-1 flex-col px-6 ${
          // Tabbed mode sits inline in a page that already scrolls: a nested
          // scroll container would trap the wheel. overflow-x-hidden instead,
          // to contain the panel's slide-in.
          tabbed ? "overflow-x-hidden" : "overflow-y-auto"
        }`}
      >
        {/* About */}
        {people?.summary?.background ? (
          <div
            id="about"
            data-testid="person-brief-section-background"
            className={sectionCls("about")}
          >
            <SectionHeader
              icon={<User size={16} />}
              title={`About`}
              subtitle="Background, experience, and expertise"
              styleKey="about"
              after={displayName}
            />
            <div className="flex flex-col gap-5">
              {/* Bio hero */}
              <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-genesis via-white to-tertiary/30 dark:from-dark-2 dark:via-dark-2 dark:to-[#352F6A]/20 border-[0.5px] border-primary/10 dark:border-primary/20 shadow-[0_2px_12px_rgba(108,92,231,0.06)]">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl bg-primary/5 pointer-events-none" />
                <MarkdownRender
                  content={people.summary.background}
                  className="!p-0 text-[13.5px] leading-[1.7] relative z-[1]"
                />
              </div>

              {/* Experience timeline */}
              {(people.summary?.current_role ||
                (people.summary?.previous_experience &&
                  people.summary.previous_experience !== "NA")) && (
                <div className="flex flex-col gap-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-text-gray-300 mb-3">
                    Experience
                  </p>
                  <div className="flex gap-3">
                    {/* Timeline line */}
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-primary shadow-[0_0_0_3px_rgba(108,92,231,0.15)] mt-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                      {people.summary?.previous_experience &&
                        people.summary.previous_experience !== "NA" && (
                          <>
                            <div className="w-[2px] flex-1 my-1 bg-gradient-to-b from-primary/40 via-primary/20 to-gray-200 dark:to-border min-h-[24px]" />
                            <div className="w-3 h-3 rounded-full flex-shrink-0 bg-gray-300 dark:bg-dark-2 border-2 border-gray-400 dark:border-border mt-0" />
                          </>
                        )}
                    </div>
                    <div className="flex flex-col gap-3 flex-1 pb-1">
                      {people.summary?.current_role && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary dark:text-primary-light mb-1.5">
                            {t("person_brief.label_current_role")}
                          </p>
                          <div
                            className="bg-white dark:bg-dark-2 rounded-lg p-3.5 border-[0.5px] border-light-border dark:border-border shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                            data-test="current-role"
                            data-testid="person-brief-section-current-role"
                          >
                            <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6">
                              {people.summary.current_role}
                            </p>
                          </div>
                        </div>
                      )}
                      {people.summary?.previous_experience &&
                        people.summary.previous_experience !== "NA" && (
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-text-gray-300 mb-1.5">
                              {t("person_brief.label_previous_experience")}
                            </p>
                            <div
                              className="bg-[#fafafa] dark:bg-dark-1/40 rounded-lg p-3.5 border-[0.5px] border-light-border dark:border-border"
                              data-test="previous-experience"
                              data-testid="person-brief-section-previous-experience"
                            >
                              <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6">
                                {people.summary.previous_experience}
                              </p>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Education + Skills grouped */}
              {(people.summary?.education_and_expertise ||
                people.skills?.length > 0) && (
                <div className="rounded-xl bg-[#fafafa] dark:bg-dark-1/40 p-4 border-[0.5px] border-light-border dark:border-border">
                  {people.summary?.education_and_expertise && (
                    <div
                      className={`flex gap-3 ${people.skills?.length > 0 ? "pb-4 mb-4 border-b border-light-border/60 dark:border-border/60" : ""}`}
                      data-test="education-and-expertise"
                      data-testid="person-brief-section-education-and-expertise"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          background: "rgba(108,92,231,0.10)",
                          color: "#6c5ce7",
                          boxShadow: "0 1px 3px rgba(108,92,231,0.12)"
                        }}
                      >
                        <GraduationCap size={14} />
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-text-gray-300 mb-1">
                          {t("person_brief.label_education_and_expertise")}
                        </p>
                        <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6">
                          {people.summary.education_and_expertise}
                        </p>
                      </div>
                    </div>
                  )}
                  {people.skills?.length > 0 && (
                    <div data-test="skills">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-text-gray-300 mb-2">
                        {t("person_brief.label_skills")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {people.skills
                          .slice(0, 8)
                          .map((skill: any, i: number) => (
                            <span
                              key={i}
                              className="bg-white dark:bg-dark-2 border-[0.5px] border-light-border dark:border-border rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.06)] px-3 py-1.5 text-xs text-dark-2 dark:text-dark-6 transition-all hover:border-primary/30 hover:shadow-[0_1px_4px_rgba(108,92,231,0.08)]"
                            >
                              {typeof skill === "string"
                                ? skill
                                : skill?.name || String(skill)}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* News */}
              {combinedNews.length > 0 && (
                <div data-test="news-posts">
                  <div className="flex items-center gap-2 mb-3">
                    <Newspaper
                      size={14}
                      className="text-gray-500 dark:text-text-gray-300"
                    />
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-text-gray-300">
                      News &amp; Posts About {displayName.split(" ")[0]}
                    </p>
                  </div>
                  <Section
                    label={`News & Posts About ${displayName.split(" ")[0]}`}
                    data={combinedNews}
                    type="rich_array_horizontal"
                    id="news_posts"
                  />
                </div>
              )}
            </div>
          </div>
        ) : streaming ? (
          <div
            id="about"
            data-testid="person-brief-summary-skeleton"
            className={sectionCls("about")}
          >
            <SectionHeader
              icon={<User size={16} />}
              title={`About`}
              subtitle="Background, experience, and expertise"
              styleKey="about"
              after={displayName}
            />
            <div className="flex flex-col gap-5">
              <div className="rounded-2xl p-5 bg-[#fafafa] dark:bg-dark-2 border-[0.5px] border-light-border dark:border-border">
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              </div>
            </div>
          </div>
        ) : null}

        {/* Talking points */}
        {people?.summary?.meeting_tips ? (
          <div
            id="talking_points"
            data-testid="person-brief-section-meeting-tips"
            className={sectionCls("talking_points")}
          >
            <SectionHeader
              icon={<Lightbulb size={16} />}
              title={t("person_brief.label_talking_points")}
              subtitle="Key conversation starters for your meeting"
              styleKey="talking_points"
            />
            <Section
              label={t("person_brief.label_talking_points")}
              data={people.summary.meeting_tips}
              type="talking_points"
            />
          </div>
        ) : streaming ? (
          <div
            id="talking_points"
            data-testid="person-brief-talking-points-skeleton"
            className={sectionCls("talking_points")}
          >
            <SectionHeader
              icon={<Lightbulb size={16} />}
              title={t("person_brief.label_talking_points")}
              subtitle="Key conversation starters for your meeting"
              styleKey="talking_points"
            />
            <Skeleton active title={false} paragraph={{ rows: 3 }} />
          </div>
        ) : null}

        {/* Key account priorities */}
        {validPriorities.length > 0 && (
          <div
            id="key_account_priorities"
            className={sectionCls("key_account_priorities")}
          >
            <SectionHeader
              icon={<Target size={16} />}
              title="Key Priorities"
              subtitle="Account priorities this person is associated with"
              styleKey="key_account_priorities"
              after={`for ${displayName}`}
            />
            <Section
              label={t("person_brief.label_top_priorities")}
              data={validPriorities}
              type="priorities_array"
            />
          </div>
        )}

        {/* Know your prospect */}
        {personalityTraits && guidelines && (
          <div
            id="know_your_prospect"
            data-test="know-your-prospect"
            className={sectionCls("know_your_prospect")}
          >
            <SectionHeader
              icon={<Crosshair size={16} />}
              title={t("person_brief.label_know_your_prospect")}
              subtitle="Personality insights and communication style"
              styleKey="know_your_prospect"
            />
            <div className="flex flex-col gap-5">
              <Section
                label={t("person_brief.label_personality_traits")}
                data={personalityTraits}
                type="personality_traits"
                id="personality-traits"
              />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-text-gray-300 mb-3">
                  {t("person_brief.label_guidelines_for_communication")}
                </p>
                <Section
                  label={t("person_brief.label_guidelines_for_communication")}
                  data={guidelines}
                  type="heading_table"
                  id="guidelines-for-communication"
                />
              </div>
            </div>
          </div>
        )}

        {/* Account Brief (lazy) */}
        {account && (
          <div id="account_brief" className={sectionCls("account_brief")}>
            <SectionHeader
              icon={<BookOpen size={16} />}
              title="Account Brief"
              subtitle={`Intelligence summary for ${account.name || "this account"}`}
              styleKey="account_brief"
            />
            <Suspense fallback={null}>
              <AccountBrief account={account} />
            </Suspense>
          </div>
        )}

        {newSectionsEnabled && (
          <>
            {/* Wrapped rather than gated: each of these owns its own query and
                renders its own block, so hiding the wrapper leaves the fetch
                running and the tab bar above still knows the section exists. */}
            <div className={panelCls("coaching_pack")}>
              <CoachingPackSection
                ctx={pmbCtx}
                enabled={personSectionsEnabled}
              />
            </div>
            <div className={panelCls("tech_stack")}>
              <TechStackSection ctx={pmbCtx} enabled={accountSectionsEnabled} />
            </div>
            <div className={panelCls("industry_trends")}>
              <IndustryTrendsSection
                ctx={pmbCtx}
                enabled={accountSectionsEnabled}
              />
            </div>
            <div className={panelCls("social_media")}>
              <SocialMediaSection
                ctx={pmbCtx}
                enabled={personSectionsEnabled}
              />
            </div>
          </>
        )}

        {/* Bottom spacer */}
        <div className="h-6" />
      </div>
    </div>
  )
}

export default PersonBrief
